import { useState, type ReactNode } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Plus } from 'lucide-react';
import { useRaiseIssue } from '@/services/queries';
import { ApiError } from '@/services/api/client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';
import type { RaiseIssueRequest } from '@/types';

const PORTALS = ['admin', 'production', 'user', 'desktop'] as const;
const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;

/** Mirrors the server's `z.string().trim().min(1).max(200)`. */
const SUBJECT_MAX = 200;
const DESCRIPTION_MAX = 4000;

const schema = Yup.object({
  portal: Yup.string()
    .oneOf([...PORTALS])
    .required('Choose which portal this affects'),
  subject: Yup.string().trim().max(SUBJECT_MAX).required('A subject is required'),
  description: Yup.string().trim().max(DESCRIPTION_MAX),
  severity: Yup.string()
    .oneOf([...SEVERITIES])
    .required(),
});

/**
 * Raising an issue against one of the portals.
 *
 * Note what this form does NOT collect: who is reporting it. The server takes
 * that from the token, because an issue attributed to whoever the client claims
 * is not an audit trail.
 *
 * A dialog rather than a page because raising an issue is something you do
 * while looking at the list you were already reading.
 *
 * `preset` fills the form for a caller that already knows what the issue is -
 * the Crashes screen raising one from a crash group - and `trigger` replaces
 * the default button there.
 */
export function RaiseIssueDialog({
  preset,
  trigger,
}: {
  preset?: Partial<RaiseIssueRequest>;
  trigger?: ReactNode;
} = {}) {
  const [open, setOpen] = useState(false);
  const raise = useRaiseIssue();

  const form = useFormik({
    initialValues: {
      portal: preset?.portal ?? ('production' as RaiseIssueRequest['portal']),
      subject: preset?.subject ?? '',
      description: preset?.description ?? '',
      severity: preset?.severity ?? ('medium' as RaiseIssueRequest['severity']),
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      try {
        await raise.mutateAsync({
          portal: values.portal,
          subject: values.subject.trim(),
          severity: values.severity,
          // Omitted rather than sent empty: the column is nullable, and an
          // empty string would read as "described, with nothing to say".
          ...(values.description.trim() ? { description: values.description.trim() } : {}),
          // Ties the issue to the crash group it came from, so the group shows it.
          ...(preset?.crashSignature ? { crashSignature: preset.crashSignature } : {}),
        });
        helpers.resetForm();
        setOpen(false);
      } catch (error) {
        /*
         * A 422 is mapped back onto the fields that caused it. Anything else is
         * surfaced by the mutation's own error handling - a form is the wrong
         * place to explain a 500.
         */
        if (error instanceof ApiError && error.fieldErrors) {
          const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), form.values);
          helpers.setErrors(placed);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Closing a half-filled form and reopening it should start clean rather
        // than resume something the person had already abandoned.
        if (!next) form.resetForm();
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button className="ml-auto">
            <Plus className="size-4" />
            Raise issue
          </Button>
        )}
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={form.handleSubmit}>
          <DialogHeader>
            <DialogTitle>Raise an issue</DialogTitle>
            <DialogDescription>
              Something wrong with one of the portals. Tracked here, not in the product.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="issue-subject">Subject</Label>
              <Input
                id="issue-subject"
                name="subject"
                value={form.values.subject}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                maxLength={SUBJECT_MAX}
                aria-invalid={Boolean(form.touched.subject && form.errors.subject)}
              />
              {form.touched.subject && form.errors.subject && (
                <p className="text-sm text-destructive">{form.errors.subject}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="issue-portal">Portal</Label>
                <Select
                  value={form.values.portal}
                  onValueChange={(value) => {
                    void form.setFieldValue('portal', value);
                  }}
                >
                  <SelectTrigger id="issue-portal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PORTALS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="issue-severity">Severity</Label>
                <Select
                  value={form.values.severity}
                  onValueChange={(value) => {
                    void form.setFieldValue('severity', value);
                  }}
                >
                  <SelectTrigger id="issue-severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="issue-description">Description</Label>
              <textarea
                id="issue-description"
                name="description"
                rows={4}
                value={form.values.description}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                maxLength={DESCRIPTION_MAX}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
              {form.touched.description && form.errors.description && (
                <p className="text-sm text-destructive">{form.errors.description}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.isSubmitting || raise.isPending}>
              Raise issue
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
